import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { SettingsNavComponent } from '../settings-nav/settings-nav';
import { BrnService } from '../../services/brn.service';

@Component({
  selector: 'app-api-keys',
  standalone: true,
  imports: [CommonModule, FormsModule, SettingsNavComponent],
  templateUrl: './api-keys.component.html',
  styleUrls: ['./api-keys.component.scss']
})
export class ApiKeysComponent implements OnInit, OnDestroy {
  private openaiKeySubject = new Subject<string>();
  private anthropicKeySubject = new Subject<string>();
  private klaviyoKeySubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  
  botId: string = '';
  
  // API Keys
  openaiApiKey: string = '';
  anthropicApiKey: string = '';
  klaviyoApiKey: string = '';
  
  // Connection status
  hasOpenAIKey: boolean = false;
  hasAnthropicKey: boolean = false;
  hasKlaviyoKey: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private brnService: BrnService
  ) {
    // Setup debounced save for OpenAI key
    this.openaiKeySubject.pipe(
      debounceTime(1200), 
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.saveOpenAIKey();
    });

    // Setup debounced save for Anthropic key
    this.anthropicKeySubject.pipe(
      debounceTime(1200), 
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.saveAnthropicKey();
    });

    // Setup debounced save for Klaviyo key
    this.klaviyoKeySubject.pipe(
      debounceTime(1200), 
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.saveKlaviyoKey();
    });
  }

  async ngOnInit() {
    this.botId = this.route.snapshot.paramMap.get('bot_id') || '';
    
    await Promise.all([
      this.checkOpenAIService(),
      this.checkAnthropicService(),
      this.checkKlaviyoService()
    ]);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onNavigationChange(item: string): void {
    if (item === 'integrations') {
      this.router.navigate(['/store', this.botId]);
    }
    // Add other navigation logic as needed
  }

  // Copy functionality
  copyOpenAIKey() {
    if (this.openaiApiKey) {
      navigator.clipboard.writeText(this.openaiApiKey).then(() => {
        // You could add a toast notification here
        console.log('OpenAI API key copied to clipboard');
      });
    }
  }

  copyAnthropicKey() {
    if (this.anthropicApiKey) {
      navigator.clipboard.writeText(this.anthropicApiKey).then(() => {
        // You could add a toast notification here
        console.log('Anthropic API key copied to clipboard');
      });
    }
  }

  copyKlaviyoKey() {
    if (this.klaviyoApiKey) {
      navigator.clipboard.writeText(this.klaviyoApiKey).then(() => {
        // You could add a toast notification here
        console.log('Klaviyo API key copied to clipboard');
      });
    }
  }

  // OpenAI methods
  onOpenAIKeyChange() {
    this.hasOpenAIKey = !!this.openaiApiKey.trim();
    this.openaiKeySubject.next(this.openaiApiKey);
  }

  async saveOpenAIKey() {
    if (this.openaiApiKey.includes('**')) {
      return;
    }

    try {
      let response;
      
      try {
        // Try to get the existing service
        response = await firstValueFrom(this.brnService.getOpenAIService(this.botId));
        // If service exists, update it (pass null if empty to trigger delete)
        response = await firstValueFrom(this.brnService.updateOpenAIService(this.botId, this.openaiApiKey.trim() || null));
      } catch (error: any) {
        // If 404 error, service doesn't exist so create it
        if (error.status === 404) {
          response = await firstValueFrom(this.brnService.createOpenAIService(this.botId, this.openaiApiKey));
        } else {
          throw error; // Re-throw other errors
        }
      }

      if (response?.status) {
        console.log('OpenAI service configured successfully');
        this.hasOpenAIKey = !!this.openaiApiKey.trim();
      } else {
        alert('Failed to save OpenAI API key. Please try again.');
      }
    } catch (error: any) {
      console.error('Error saving OpenAI API key:', error);
      alert('Failed to save OpenAI API key. Please try again.');
    }
  }


  async checkOpenAIService() {
    try {
      const response = await firstValueFrom(this.brnService.getOpenAIService(this.botId));
      if (response.status && response.service?.data?.api_key) {
        this.openaiApiKey = response.service.data.api_key;
        this.hasOpenAIKey = true;
      }
    } catch (error: any) {
      // Service doesn't exist yet, that's okay
      console.log('OpenAI service not configured yet');
      this.hasOpenAIKey = false;
    }
  }

  // Anthropic methods
  onAnthropicKeyChange() {
    this.hasAnthropicKey = !!this.anthropicApiKey.trim();
    this.anthropicKeySubject.next(this.anthropicApiKey);
  }

  async saveAnthropicKey() {
    if (this.anthropicApiKey.includes('**')) {
      return;
    }

    try {
      let response;
      
      try {
        // Try to get the existing service
        response = await firstValueFrom(this.brnService.getAnthropicService(this.botId));
        // If service exists, update it (pass null if empty to trigger delete)
        response = await firstValueFrom(this.brnService.updateAnthropicService(this.botId, this.anthropicApiKey.trim() || null));
      } catch (error: any) {
        // If 404 error, service doesn't exist so create it
        if (error.status === 404) {
          response = await firstValueFrom(this.brnService.createAnthropicService(this.botId, this.anthropicApiKey));
        } else {
          throw error; // Re-throw other errors
        }
      }

      if (response?.status) {
        console.log('Anthropic service configured successfully');
        this.hasAnthropicKey = !!this.anthropicApiKey.trim();
      } else {
        alert('Failed to save Anthropic API key. Please try again.');
      }
    } catch (error: any) {
      console.error('Error saving Anthropic API key:', error);
      alert('Failed to save Anthropic API key. Please try again.');
    }
  }


  async checkAnthropicService() {
    try {
      const response = await firstValueFrom(this.brnService.getAnthropicService(this.botId));
      if (response.status && response.service?.data?.api_key) {
        this.anthropicApiKey = response.service.data.api_key;
        this.hasAnthropicKey = true;
      }
    } catch (error: any) {
      // Service doesn't exist yet, that's okay
      console.log('Anthropic service not configured yet');
      this.hasAnthropicKey = false;
    }
  }

  // Klaviyo methods
  onKlaviyoKeyChange() {
    this.hasKlaviyoKey = !!this.klaviyoApiKey.trim();
    this.klaviyoKeySubject.next(this.klaviyoApiKey);
  }

  async saveKlaviyoKey() {
    if (!this.klaviyoApiKey.trim() || this.klaviyoApiKey.includes('**')) {
      return;
    }

    try {
      let response;
      
      try {
        // Try to get the existing service
        response = await firstValueFrom(this.brnService.getKlaviyoService(this.botId));
        // If service exists, update it
        response = await firstValueFrom(this.brnService.updateKlaviyoService(this.botId, this.klaviyoApiKey));
      } catch (error: any) {
        // If 404 error, service doesn't exist so create it
        if (error.status === 404) {
          response = await firstValueFrom(this.brnService.createKlaviyoService(this.botId, this.klaviyoApiKey));
        } else {
          throw error; // Re-throw other errors
        }
      }

      if (response?.status) {
        console.log('Klaviyo service configured successfully');
        this.hasKlaviyoKey = !!this.klaviyoApiKey.trim();
      } else {
        alert('Failed to save Klaviyo API key. Please try again.');
      }
    } catch (error: any) {
      console.error('Error saving Klaviyo API key:', error);
      alert('Failed to save Klaviyo API key. Please try again.');
    }
  }

  async checkKlaviyoService() {
    try {
      const response = await firstValueFrom(this.brnService.getKlaviyoService(this.botId));
      if (response.status && response.service?.data?.api_key) {
        this.klaviyoApiKey = response.service.data.api_key;
        this.hasKlaviyoKey = true;
      }
    } catch (error: any) {
      // Service doesn't exist yet, that's okay
      console.log('Klaviyo service not configured yet');
      this.hasKlaviyoKey = false;
    }
  }

}
